import React from 'react';
import { Box, Typography } from '@mui/material';
import { LuFileText, LuBriefcase, LuMonitorPlay, LuClipboardCheck, LuCheck } from 'react-icons/lu';
import { elliottTokens as t, LessonStepKey, StepStatus } from '../tokens';
import Shine from './Shine';
import { NewLessonPill, UpdatedLessonPill } from './LeftNav';
import { MOBILE_SX } from '../../../../../../mobile/mobileApp';

export interface Step {
  key: LessonStepKey;
  label: string;
  status: StepStatus;
  /** Course versioning: this part of the lesson was added / changed by a publish. */
  badge?: 'new' | 'updated';
}

const STEP_ICON: Record<LessonStepKey, React.ComponentType<{ size?: number; color?: string }>> = {
  content: LuFileText,
  application: LuBriefcase,
  simulation: LuMonitorPlay,
  quiz: LuClipboardCheck
};

// Default step definitions. Keys stay as they are; the labels say what the
// learner actually does in each step (Case Studies, Live Scenario).
export const DEFAULT_STEPS: Omit<Step, 'status'>[] = [
  { key: 'content', label: 'Lesson Body' },
  { key: 'application', label: 'Case studies' },
  { key: 'simulation', label: 'Roleplay' },
  { key: 'quiz', label: 'Quiz' }
];

const StepNode: React.FC<{ step: Step }> = ({ step }) => {
  // Complete steps show a check; all other steps keep their own icon. Icons are always orange.
  const Icon = step.status === 'complete' ? LuCheck : STEP_ICON[step.key];

  const circleSx =
    step.status === 'active'
      ? {
          // Current step: orange tint fill + outer & inner glow (exact Figma — no fading ring).
          bgcolor: 'rgba(247,148,32,0.1)',
          boxShadow: '0px 0px 16px 0px rgba(244,119,35,0.5), inset 0px 0px 8px 0px rgba(247,148,32,0.25)'
        }
      : step.status === 'complete'
      ? { border: `1.5px solid ${t.brandLight}` }
      : { border: `1.5px solid ${t.borderDim}` }; // upcoming → grey circle

  return (
    <Box
      sx={{
        width: 72,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: '8px',
        [MOBILE_SX]: { width: 68, flexShrink: 0, gap: '6px' }
      }}
    >
      <Box
        sx={{
          position: 'relative',
          overflow: 'hidden',
          width: 38,
          height: 38,
          [MOBILE_SX]: { width: 34, height: 34 },
          borderRadius: '50%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          ...circleSx
        }}
      >
        {step.status === 'active' && <Shine />}
        <Box sx={{ position: 'relative', zIndex: 1, display: 'flex' }}>
          {/* Orange icons on every step, in all states. */}
          <Icon size={20} color={t.brandLight} />
        </Box>
      </Box>
      <Typography
        sx={{
          fontSize: 14,
          lineHeight: '17px',
          fontWeight: 500,
          whiteSpace: 'nowrap',
          // Phone: a two-word step name may take two lines rather than run into the next step.
          [MOBILE_SX]: { fontSize: 12, lineHeight: '15px', whiteSpace: 'normal', textAlign: 'center' },
          // Current step label uses the brandy gradient; to-do is grey; complete is white.
          ...(step.status === 'active'
            ? {
                backgroundImage: t.brandyTextGradient,
                WebkitBackgroundClip: 'text',
                backgroundClip: 'text',
                color: 'transparent'
              }
            : { color: step.status === 'upcoming' ? t.textTertiary : t.textPrimary })
        }}
      >
        {step.label}
      </Typography>
      {step.badge && (step.badge === 'new' ? <NewLessonPill /> : <UpdatedLessonPill />)}
    </Box>
  );
};

const Connector: React.FC<{ filled: boolean }> = ({ filled }) => (
  <Box
    sx={{
      flex: 1,
      minWidth: 24,
      height: 32,
      display: 'flex',
      alignItems: 'flex-start',
      pt: '17px',
      [MOBILE_SX]: { minWidth: 8, pt: '16px' }
    }}
  >
    <Box sx={{ width: '100%', height: 2, borderRadius: 1, bgcolor: filled ? t.brandLight : t.borderDim }} />
  </Box>
);

const StepStepper: React.FC<{ steps: Step[] }> = ({ steps }) => (
  <Box
    sx={{
      display: 'flex',
      alignItems: 'flex-start',
      justifyContent: 'center',
      width: '100%',
      maxWidth: 1146,
      mx: 'auto',
      px: '6px'
    }}
  >
    {steps.map((step, i) => (
      <React.Fragment key={step.key}>
        <StepNode step={step} />
        {i < steps.length - 1 && <Connector filled={steps[i].status === 'complete'} />}
      </React.Fragment>
    ))}
  </Box>
);

export default StepStepper;
